/*
  Keyframe animations, described once and played by whichever renderer is drawing.

  The shape here is deliberately the intersection of two APIs that already agree: the browser's Web
  Animations API takes a list of keyframes with offsets, and Reanimated can walk a 0→1 progress value
  through the same offsets. So a spec is transcribed straight into either, and the board doesn't know
  which one it's running on.

  **Easing is per segment, not per animation**, because that is what CSS does and the CSS is what the
  specs were transcribed from: `animation: dance 1.4s ease` eases each pair of keyframes in turn, so
  an eleven-frame dance eases ten times. Both renderers have to be told this explicitly — WAAPI's
  options-level easing spans the whole iteration, and a single Reanimated `withTiming` does too — and
  a spec played the other way runs for the same length while feeling quite different: the motion
  bunches into the middle instead of settling at each frame.

  Only what the boards' animations actually use is modelled — scale on each axis, rotation, opacity,
  and an offset on each axis — rather than a general transform. A property left out of a keyframe is
  interpolated from the frames either side of it, so a spec states only what moves.
*/

export interface AnimationKeyframe {
  /** Position in the timeline, 0 to 1. Frames must be listed in order, starting at 0. */
  offset: number;
  scaleX?: number;
  scaleY?: number;
  /** Degrees. */
  rotate?: number;
  /** Horizontal offset, in the same units BoxStyle uses: px on the web, density pixels on native. */
  translateX?: number;
  /** Vertical offset, in the same units BoxStyle uses: px on the web, density pixels on native. */
  translateY?: number;
  opacity?: number;
}

export interface AnimationSpec {
  keyframes: AnimationKeyframe[];
  /** Milliseconds. */
  duration: number;
  /** Milliseconds to wait before the first frame; the element holds that frame meanwhile. */
  delay?: number;
  /**
   * How many times it plays. One unless it says otherwise, and `'infinite'` for something that runs
   * until it is taken off screen — a prompt that keeps asking rather than a motion that completes.
   * Spelled out rather than borrowed from either renderer, since they disagree: WAAPI wants
   * `Infinity` and Reanimated wants `-1`.
   */
  iterations?: number | 'infinite';
  /**
   * The CSS timing functions the web keyframes name, or the four control points of a cubic bezier
   * for a curve with no name. Linear unless it says otherwise.
   */
  easing?: 'linear' | 'ease' | 'ease-out' | CubicBezier;
  /**
   * What the transform pivots around. `bottom` is the dance's `transform-origin: bottom center`,
   * which is what makes it read as a wobble rather than a spin.
   */
  origin?: 'center' | 'bottom';
}

/** The properties a spec can animate. */
export type AnimatedProperty = 'scaleX' | 'scaleY' | 'rotate' | 'translateX' | 'translateY' | 'opacity';

/** What a property rests at when a frame leaves it out: nothing for the offsets, full for the rest. */
const REST: Record<AnimatedProperty, number> = {
  scaleX: 1,
  scaleY: 1,
  rotate: 0,
  translateX: 0,
  translateY: 0,
  opacity: 1,
};

/** The value of one property at a given frame, falling back to its resting value. */
const at = (frame: AnimationKeyframe, property: AnimatedProperty): number => frame[property] ?? REST[property];

/** The offsets of a spec's frames, for an interpolation's input range. */
export const offsetsOf = (spec: AnimationSpec): number[] => spec.keyframes.map((frame) => frame.offset);

/** One property's value at each frame, for an interpolation's output range. */
export const valuesOf = (spec: AnimationSpec, property: AnimatedProperty): number[] =>
  spec.keyframes.map((frame) => at(frame, property));

/**
 * CSS `ease`, as the control points of its cubic bezier.
 *
 * Named here because the two renderers reach it differently: the browser is handed the keyword, and
 * Reanimated is handed the curve. `Easing.inOut(Easing.quad)` is the obvious-looking stand-in and is
 * not the same shape — it is symmetric, where `ease` accelerates hard and then has a long tail, so a
 * motion played with it arrives early and reads as more hurried.
 */
export const CSS_EASE: CubicBezier = [0.25, 0.1, 0.25, 1];

/** CSS `ease-out`: quick to start and slow to arrive, which is how a mark fades in. */
export const CSS_EASE_OUT: CubicBezier = [0, 0, 0.58, 1];

/** The control points of a cubic bezier, in the order CSS and Reanimated both take them. */
export type CubicBezier = readonly [number, number, number, number];

/** The same curve spelled for CSS, which is what both the DOM keyframes and the docs use. */
export const cssEasing = (easing: AnimationSpec['easing']): string =>
  typeof easing === 'string' || easing === undefined ? (easing ?? 'linear') : `cubic-bezier(${easing.join(', ')})`;

/** The curve as control points, whatever it was named. Linear has none, and says so. */
export const bezierOf = (easing: AnimationSpec['easing']): CubicBezier | null => {
  if (easing === 'ease') return CSS_EASE;
  if (easing === 'ease-out') return CSS_EASE_OUT;
  if (easing === 'linear' || easing === undefined) return null;
  return easing;
};

/** One stretch between consecutive keyframes: where it ends, and how long it takes to get there. */
export interface AnimationSegment {
  /** The offset this segment animates to. */
  toOffset: number;
  /** Milliseconds, this segment's share of the spec's duration. */
  duration: number;
}

/**
 * A spec as the stretches between its frames, which is how both renderers have to play it for the
 * easing to land on each one. Offsets are fractions of the whole, so a segment's duration is its
 * share of the total.
 */
export const segmentsOf = (spec: AnimationSpec): AnimationSegment[] =>
  spec.keyframes.slice(1).map((frame, index) => ({
    toOffset: frame.offset,
    duration: (frame.offset - spec.keyframes[index].offset) * spec.duration,
  }));

/** How many times a renderer should repeat the spec, in the count Reanimated's `withRepeat` takes. */
export const repeatCount = (spec: AnimationSpec): number =>
  spec.iterations === 'infinite' ? -1 : (spec.iterations ?? 1);

/** The frame an element rests on before an entrance plays, or after an exit has. */
export const finalFrame = (spec: AnimationSpec): AnimationKeyframe => spec.keyframes[spec.keyframes.length - 1];
